import dayjs from 'dayjs';
import React, { Fragment, useCallback, useState } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';

import { DateRange } from '@mui/icons-material';
import { DatePickerProps, DesktopDatePicker, LocalizationProvider, MobileDatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Box, IconButton, InputAdornment, TextField } from '@mui/material';

import { CommonFieldProps, DateTimeFieldProps } from './props/FieldProps';
import { Title } from './Widgets/Title';
import { translate } from 'react-jhipster';

export interface StandardDatePickerProps extends CommonFieldProps<'date-picker'>, DateTimeFieldProps {
  attribute: Required<CommonFieldProps<'date-picker'>>['attribute'];
}

export default function StandardDatePicker(props: {
  field: StandardDatePickerProps;
  methods: UseFormReturn;
  hideTitle?: boolean;
}) {
  const {
    field: fieldConfig,
    methods: {
      control,
      getValues,
      setValue,
      trigger,
      formState: { errors },
    },
    hideTitle,
  } = props;
  const [open, setOpen] = useState<boolean>();

  const component = useCallback(
    (datePickerProps: DatePickerProps) => {
      if (fieldConfig.keyboard) {
        return <DesktopDatePicker {...datePickerProps} />;
      }
      return <MobileDatePicker {...datePickerProps} />;
    },
    [fieldConfig.keyboard],
  );

  const componentProps = (fieldConf: StandardDatePickerProps, value?: string): DatePickerProps => {
    return {
      format: 'DD/MM/YYYY',
      label: fieldConf.label && translate(fieldConf.label, null, fieldConf.label),
      open: !!open,
      onClose: () => setOpen(false),
      ...fieldConf.props,
      value: value ? dayjs(value, 'YYYY-MM-DD') : null,
      onChange(valueIn) {
        if (valueIn) {
          try {
            const formatted = dayjs(valueIn).format('YYYY-MM-DD');
            setValue(fieldConf.attribute, formatted);
          } catch (error) {}
        } else {
          setValue(fieldConf.attribute, undefined);
        }
        if (fieldConf.props.onChange) fieldConf.props.onChange(valueIn);
      },
      slotProps: {
        textField: {
          fullWidth: true,
          size: 'small',
          onClick: () => setOpen(true),
          error: !!errors[fieldConf.attribute],
          helperText: <>{errors[fieldConf.attribute]?.message}</>,
          onKeyDown(event) {
            if (event.key === 'Enter') {
              trigger(fieldConf.attribute);
            }
          },
          InputProps: {
            endAdornment: (
              <InputAdornment position="end">
                <IconButton aria-label="open date picker" size="large">
                  <DateRange />
                </IconButton>
              </InputAdornment>
            ),
            sx: {
              pr: fieldConf.keyboard ? undefined : 0,
            },
          },
        },
      },
    };
  };

  return (
    <Controller
      name={fieldConfig.attribute}
      control={control}
      render={({ field }) => (
        <Fragment>
          {!hideTitle && fieldConfig.title && <Title field={fieldConfig} />}
          <Box>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              {component(componentProps(fieldConfig, field.value))}
            </LocalizationProvider>
          </Box>
        </Fragment>
      )}
    />
  );
}
